import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useEmployeeSession } from "@/hooks/useEmployeeSession";
import { EMPLOYEE_READ_ONLY_TEXT, isEmployeeReadOnlyCode } from "@/lib/accountPolicy";
import { useMonteurT } from "@/i18n/monteur";
import {
  GraduationCap,
  Search,
  Section,
  Hand,
  IdCard,
  Settings,
  ChevronRight,
  LogOut,
} from "lucide-react";

/**
 * KARTEN-HOME (Juni 2026, betreibergenehmigt) — Mitarbeiter-Hub nach Login.
 * Dunkler Hintergrund (konsistent zum PIN-Login), weiße Funktionskarten mit
 * farbigem Icon-Badge, roter Zähler-Bubble, Titel/Untertitel und Chevron.
 * WICHTIG: Rein lesend — markTasksSeen wird hier bewusst NICHT aufgerufen,
 * "gesehen" setzt erst das Öffnen der Unterweisungsliste (/monteur/aufgaben).
 */
function HomeCard({ to, testid, icon: Icon, iconClass, title, subtitle, bubble = 0, bubbleAria, statusAttr }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      {...(statusAttr ? { "data-fuehrerschein-status": statusAttr } : {})}
      className="flex items-center gap-4 rounded-2xl bg-white px-4 py-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
    >
      <span className={`relative w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconClass}`}>
        <Icon className="w-6 h-6" />
        {bubble > 0 && (
          <span
            data-testid={`${testid}-bubble`}
            aria-label={bubbleAria}
            className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white"
          >
            {bubble}
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-heading font-semibold text-slate-900 text-base leading-snug">
          {title}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</div>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
    </Link>
  );
}

export default function MonteurHome() {
  const navigate = useNavigate();
  const { session, logout } = useEmployeeSession();
  const t = useMonteurT();
  const employee = session?.employee;
  const employeeName = employee
    ? `${employee.first_name} ${employee.last_name}`.trim()
    : t("common.loading");
  const companyName = employee?.company_name || t("tasks.companyFallback");
  const [assignments, setAssignments] = useState([]);
  const [driverTask, setDriverTask] = useState(null);
  const [handschutzplaene, setHandschutzplaene] = useState([]);

  useEffect(() => {
    if (!session?.employee?.id) return;
    // Rein lesend (GET ist seiteneffektfrei) — KEIN markTasksSeen hier.
    api.listMyAssignments().then(setAssignments).catch(() => {});
    api
      .getMyDriverTask()
      .then((data) => setDriverTask(data))
      .catch(() => setDriverTask(null));
    api
      .getMyHandschutzplaene()
      .then((rows) => setHandschutzplaene(rows || []))
      .catch(() => setHandschutzplaene([]));
  }, [session]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const offene = assignments.filter((a) => a.status !== "erledigt");
  const neueCount = offene.filter((a) => a.is_new).length;
  const driverSubtitle =
    driverTask?.status === "open"
      ? t("home.driverOpen")
      : driverTask?.status === "submitted"
        ? t("home.driverSubmitted")
        : t("home.driverUpToDate");

  return (
    <div className="min-h-screen bg-slate-900" data-testid="monteur-home">
      <header className="max-w-lg mx-auto px-5 pt-8 pb-6 relative">
        <button
          onClick={handleLogout}
          className="absolute right-4 top-8 text-slate-400 hover:text-white p-2 transition-colors"
          data-testid="monteur-logout"
          aria-label={t("tasks.logoutAria")}
        >
          <LogOut className="w-5 h-5" />
        </button>
        <img
          src="/brand/clartext-logo-full-dark.svg"
          alt="CLartext"
          className="h-12 w-auto max-w-[190px] object-contain mx-auto"
          data-testid="monteur-brand-logo"
        />
        <div className="mt-5 text-center">
          <div
            className="font-heading text-lg font-bold text-white leading-snug break-words"
            data-testid="monteur-company-name"
          >
            {companyName}
          </div>
          <div className="text-sm text-slate-400" data-testid="monteur-portal-subtitle">
            {t("tasks.portalSubtitle")}
          </div>
        </div>
        {/* FIX-01b-2 (E9, Betreiberfreigabe 12.10.): dezenter Lesezugriff-Hinweis aus
            access_state (GET /me); bei suspended bestehendes Verhalten (kein Hinweis). */}
        {employee?.access_state?.read_only &&
          isEmployeeReadOnlyCode(employee.access_state.reason) && (
            <div
              className="mt-3 mx-auto max-w-md rounded-md border border-amber-300/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
              data-testid="monteur-readonly-hint"
            >
              {EMPLOYEE_READ_ONLY_TEXT}
            </div>
          )}
        <div
          className="mt-3 flex items-center justify-center gap-2"
          data-testid="monteur-current-user"
        >
          {employee?.photo_url && (
            <img
              src={employee.photo_url}
              alt=""
              className="w-7 h-7 rounded-full object-cover bg-slate-700 shrink-0"
            />
          )}
          <div className="text-xs text-slate-400 min-w-0">
            {t("tasks.loggedInAs")}{" "}
            <span className="font-semibold text-slate-200">{employeeName}</span>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 pb-10 space-y-3">
        <HomeCard
          to="/monteur/aufgaben"
          testid="home-card-tasks"
          icon={GraduationCap}
          iconClass="bg-sky-100 text-sky-700"
          title={t("home.tasksTitle")}
          subtitle={t("tasks.openCount", { count: offene.length })}
          bubble={neueCount}
          bubbleAria={t("home.newBubbleAria", { count: neueCount })}
        />

        <HomeCard
          to="/monteur/suche"
          testid="home-card-suche"
          icon={Search}
          iconClass="bg-violet-100 text-violet-700"
          title={t("home.searchTitle")}
          subtitle={t("home.searchSubtitle")}
        />

        <HomeCard
          to="/monteur/aushang"
          testid="monteur-aushang-link"
          icon={Section}
          iconClass="bg-amber-100 text-amber-700"
          title={t("aushang.linkTitle")}
          subtitle={t("aushang.linkSubtitle")}
        />

        {handschutzplaene.map((hsp) => (
          <HomeCard
            key={hsp.einsatzbereich_id}
            to={`/monteur/handschutzplan/${hsp.einsatzbereich_id}`}
            testid={`monteur-handschutzplan-link-${hsp.einsatzbereich_id}`}
            icon={Hand}
            iconClass="bg-teal-100 text-teal-700"
            title={
              hsp.einsatzbereich_name
                ? `${t("home.hspTitle")} – ${hsp.einsatzbereich_name}`
                : t("home.hspTitle")
            }
            subtitle={t("home.hspSubtitle")}
          />
        ))}

        {driverTask && driverTask.status !== "not_applicable" && (
          <HomeCard
            to="/monteur/fuehrerschein"
            testid="monteur-fuehrerschein-link"
            statusAttr={driverTask.status}
            icon={IdCard}
            iconClass="bg-indigo-100 text-indigo-700"
            title={t("home.driverTitle")}
            subtitle={driverSubtitle}
            bubble={driverTask.status === "open" ? 1 : 0}
            bubbleAria={t("home.driverOpen")}
          />
        )}

        {/* Mitarbeiter-Einstellungen Teil 2 (Juni 2026): echte Seite. */}
        <HomeCard
          to="/monteur/einstellungen"
          testid="home-card-einstellungen"
          icon={Settings}
          iconClass="bg-slate-200 text-slate-600"
          title={t("home.settingsTitle")}
          subtitle={t("home.settingsSubtitle")}
        />
      </div>
    </div>
  );
}
